import { ViewConfiguration } from '../../../grid/gridView/types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { translateFn } from '../../../utils/translator.helper';
import { IntEnumConditions } from '../../../grid/gridFilters/types';

export interface MdrSecurityTelemetryExclusionsWebComponentSchema {
  view: ViewConfiguration<Column, ColumnKeys>;
  columns: Array<ColumnDetails<Column, ColumnKeys>>;
}

export interface SecurityTelemetryExclusionGridItem {
  fieldType: string;
  excludedItem: string;
}

export interface GetDataGridItemsResponse {
  rawEvents?: Array<SecurityTelemetryExclusionGridItem>;
}

export interface SetFiltersRequest {
  viewId: string;
  filters: Array<Filter>;
}

interface Filter {
  companyId: IntEnumConditions;
  eventType: IntEnumConditions;
  field: IntEnumConditions;
}

export enum ColumnKeys {
  COMPANY = 'companyId',
  EXCLUSION_EVENT_TYPE = 'eventType',
  EXCLUSION_FIELD = 'field',
  EXCLUSION_EXCLUDED_ITEM = 'excludedItem',
}

export enum Column {
  COMPANY,
  EXCLUSION_EVENT_TYPE,
  EXCLUSION_FIELD,
  EXCLUSION_EXCLUDED_ITEM,
}

export const DISPLAYED_FILTERS = [
  { [ColumnKeys.EXCLUSION_EVENT_TYPE]: { in: [] } },
  { [ColumnKeys.EXCLUSION_FIELD]: { in: [] } },
];

export const PREDEFINED_FILTERS_KEYS = [
  ColumnKeys.COMPANY,
  ColumnKeys.EXCLUSION_EVENT_TYPE,
  ColumnKeys.EXCLUSION_FIELD,
];

export const DISPLAYED_COLUMNS = [Column.EXCLUSION_EVENT_TYPE, Column.EXCLUSION_FIELD, Column.EXCLUSION_EXCLUDED_ITEM];

export function getColumnDetails(): ColumnDetails<Column, ColumnKeys>[] {
  return [
    {
      column: Column.COMPANY,
      key: ColumnKeys.COMPANY,
      columnName: translateFn('Company'),
      type: ColumnType.REMOTE_SELECT,
      removable: false,
      sortable: true,
      showNone: true,
      isHiddenInGridSettings: true,
      filterResetValue: {
        exactMatch: (Application.Main as any).user.company.id ?? null,
      },
    },
    {
      column: Column.EXCLUSION_EVENT_TYPE,
      key: ColumnKeys.EXCLUSION_EVENT_TYPE,
      columnName: translateFn('Event type'),
      type: ColumnType.ENUM,
      values: getEventTypeValues(),
      removable: false,
      sortable: true,
      width: '200px',
    },
    {
      column: Column.EXCLUSION_FIELD,
      key: ColumnKeys.EXCLUSION_FIELD,
      columnName: translateFn('Field'),
      type: ColumnType.ENUM,
      values: getFieldTypeValues(),
      removable: false,
      sortable: true,
      width: '200px',
    },
    {
      column: Column.EXCLUSION_EXCLUDED_ITEM,
      key: ColumnKeys.EXCLUSION_EXCLUDED_ITEM,
      columnName: translateFn('Excluded item'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
      flexGrow: 1,
      filteringDisabled: true,
      isFilter: false,
    },
  ];
}

export enum FieldTypeValues {
  PATH = 'path',
  USER_NAME = 'user_name',
  PROCESS_PATH = 'process_path',
  PARENT_PROCESS_PATH = 'parent_process_path',
  USER_SID = 'user_sid',
  KEY_PATH = 'key_path',
  SERVICE_PATH = 'service_path',
  VICTIM_PATH = 'victim_path',
  NEW_SERVICE_PATH = 'new_service_path',
}

export enum EventTypeValues {
  CREATE_PROCESS = 'CreateProcess',
  CREATE_FILE = 'CreateFile',
  DELETE_FILE = 'DeleteFile',
  MODIFY_FILE = 'ModifyFile',
  READ_FROM_FILE = 'ReadFromFile',
  MOVE_FILE = 'MoveFile',
  REG_CREATE_KEY = 'RegCreateKey',
  REG_DELETE_KEY = 'RegDeleteKey',
  REG_MODIFY_VALUE = 'RegModifyValue',
  REG_DELETE_VALUE = 'RegDeleteValue',
  NETWORK_CONNECTION = 'NetworkConnection',
  SERVICE_ADDED = 'ServiceAdded',
  SCHEDULE_TASK_CREATE = 'ScheduleTaskCreate',
  SCHEDULE_TASK_MODIFY = 'ScheduleTaskModify',
  SCHEDULE_TASK_DELETE = 'ScheduleTaskDelete',
  PROCESS_INJECTION = 'ProcessInjection',
  SERVICE_MODIFY = 'ServiceModify',
  LOGON = 'Logon',
  LOGOUT = 'Logout',
  USER_SPECIFIC_LOGGING = 'UserSpecificLogging',
  USER_ACCOUNT_SETTINGS_CHANGED = 'UserAccountSettingsChangedFields',
}

export function getFieldTypeValues() {
  return [
    {
      label: translateFn('Process Path'),
      value: FieldTypeValues.PROCESS_PATH,
    },
    {
      label: translateFn('Parent Process Path'),
      value: FieldTypeValues.PARENT_PROCESS_PATH,
    },
    {
      label: translateFn('Username'),
      value: FieldTypeValues.USER_NAME,
    },
    {
      label: translateFn('Path'),
      value: FieldTypeValues.PATH,
    },
    {
      label: translateFn('User SID'),
      value: FieldTypeValues.USER_SID,
    },
    {
      label: translateFn('Key Path'),
      value: FieldTypeValues.KEY_PATH,
    },
    {
      label: translateFn('Service Path'),
      value: FieldTypeValues.SERVICE_PATH,
    },
    {
      label: translateFn('Victim Path'),
      value: FieldTypeValues.VICTIM_PATH,
    },
    {
      label: translateFn('New Service Path'),
      value: FieldTypeValues.NEW_SERVICE_PATH,
    },
  ];
}

export function getEventTypeValues() {
  return [
    {
      label: translateFn('Create Process'),
      value: EventTypeValues.CREATE_PROCESS,
    },
    {
      label: translateFn('Create File'),
      value: EventTypeValues.CREATE_FILE,
    },
    {
      label: translateFn('Delete File'),
      value: EventTypeValues.DELETE_FILE,
    },
    {
      label: translateFn('Modify File'),
      value: EventTypeValues.MODIFY_FILE,
    },
    {
      label: translateFn('Read From File'),
      value: EventTypeValues.READ_FROM_FILE,
    },
    {
      label: translateFn('Move File'),
      value: EventTypeValues.MOVE_FILE,
    },
    {
      label: translateFn('Registry Create Key'),
      value: EventTypeValues.REG_CREATE_KEY,
    },
    {
      label: translateFn('Registry Delete Key'),
      value: EventTypeValues.REG_DELETE_KEY,
    },
    {
      label: translateFn('Registry Modify Value'),
      value: EventTypeValues.REG_MODIFY_VALUE,
    },
    {
      label: translateFn('Registry Delete Value'),
      value: EventTypeValues.REG_DELETE_VALUE,
    },
    {
      label: translateFn('Network Connection'),
      value: EventTypeValues.NETWORK_CONNECTION,
    },
    {
      label: translateFn('Service Added'),
      value: EventTypeValues.SERVICE_ADDED,
    },
    {
      label: translateFn('Schedule Task Create'),
      value: EventTypeValues.SCHEDULE_TASK_CREATE,
    },
    {
      label: translateFn('Schedule Task Modify'),
      value: EventTypeValues.SCHEDULE_TASK_MODIFY,
    },
    {
      label: translateFn('Schedule Task Delete'),
      value: EventTypeValues.SCHEDULE_TASK_DELETE,
    },
    {
      label: translateFn('Process Injection'),
      value: EventTypeValues.PROCESS_INJECTION,
    },
    {
      label: translateFn('Service Modify'),
      value: EventTypeValues.SERVICE_MODIFY,
    },
    {
      label: translateFn('Logon'),
      value: EventTypeValues.LOGON,
    },
    {
      label: translateFn('Logout'),
      value: EventTypeValues.LOGOUT,
    },
    {
      label: translateFn('User Specific Logging'),
      value: EventTypeValues.USER_SPECIFIC_LOGGING,
    },
    {
      label: translateFn('User Account Settings Changed Fields'),
      value: EventTypeValues.USER_ACCOUNT_SETTINGS_CHANGED,
    },
  ];
}
